"use client";

import React, { FC } from "react";

import { track } from "@vercel/analytics";
import slugify from "react-slugify";

import { localizedPaths, canonicalBaseLanguageURLs } from "i18n/utils";

import { FormProps } from "lib/types";
import { useCurrentLocale } from "lib/utils";

import { Button } from "./button";
import { CheckBox } from "./checkbox";
import { TextField } from "./text-field";
import { Typography } from "./typography";

const Form: FC<
  FormProps & {
    translations: {
      send: string;
    };
  }
> = ({
  bottom = true,
  color = "blue",
  eventDetails,
  formFields,
  formKey,
  headingId,
  hideTitle = false,
  title,
  translations,
  variant = "web3",
}) => {
  const locale = useCurrentLocale();

  const t = translations;

  const getBgColor: string = {
    blue: "bg-lightblue",
    white: "bg-white",
    beige: "bg-beige",
    yellow: "bg-yellow",
    red: "bg-red",
    green: "bg-green",
    brown: "bg-warningDark",
  }[color ?? "blue"];

  const eventQueryString =
    eventDetails != null
      ? `?${new URLSearchParams(
          Object.entries(eventDetails)
            .filter(([, value]) => value !== undefined && value !== null)
            .reduce(
              (acc, [key, value]) => {
                acc[key] = String(value);
                return acc;
              },
              {} as Record<string, string>,
            ),
        ).toString()}`
      : "";

  const actionString = {
    web3: "https://api.web3forms.com/submit",
    mailchimp: "https://useit.us21.list-manage.com/subscribe/post",
  }[variant ?? "web3"];

  const hiddenInputs =
    variant === "web3" ? (
      <>
        <input name="access_key" type="hidden" value={formKey ?? ""} />
        <input
          name="redirect"
          type="hidden"
          value={`${canonicalBaseLanguageURLs(locale)}${
            localizedPaths(locale).confirmation
          }${eventQueryString}`}
        />
        <input
          name="subject"
          type="hidden"
          value={`New form submission from "${title}"`}
        />
      </>
    ) : (
      <>
        <input name="u" type="hidden" value="86f8e00d37ae4ed52b6de8f14" />
        <input name="id" type="hidden" value="d8b2390523" />
      </>
    );

  return (
    <form
      action={actionString}
      className={`not-prose ${
        bottom ? "mb-[-32px] rounded-bl-2xl md:mb-[-64px]" : ""
      } ml-[-24px] w-[calc(100%_+_48px)] ${getBgColor} mt-[64px] p-6 dark:bg-transparent md:ml-[-48px] md:w-[calc(100%_+_96px)] md:p-12`}
      method="POST"
      onSubmit={() => {
        track(`Newsletter`);
      }}
    >
      {hiddenInputs}
      <div className="flex flex-col gap-8 xl:gap-12">
        {!hideTitle && (
          <Typography
            className="focus:outline-0"
            id={headingId ?? undefined}
            tabIndex={headingId ? -1 : undefined}
            variant="h2"
          >
            {title}
          </Typography>
        )}
        <div className="flex flex-col gap-4 xl:gap-6">
          {formFields?.map((field) => (
            <div key={slugify(field.label)}>
              {field.type === "textfield" || field.type === "textarea" ? (
                <TextField
                  autoComplete={field.textType}
                  id={field.id ?? ""}
                  label={field.label ?? ""}
                  required={field.required}
                  variant={field.type === "textarea" ? "textarea" : "text"}
                />
              ) : (
                <CheckBox
                  defaultChecked={field.defaultChecked}
                  id={field.id ?? ""}
                  label={field.label ?? ""}
                  labelContent={field.labelContent ?? undefined}
                  required={field.required}
                />
              )}
            </div>
          ))}
        </div>
        <Button align="center" name="submit" type="submit" value="Subscribe">
          {t.send}
        </Button>
      </div>
    </form>
  );
};

export default Form;
